import React from "react";
import { bool, string, number, oneOfType, arrayOf, oneOf } from "prop-types";
import cx from "classnames";
import styles from "./Accordion.module.scss";
import { useAccordion, useAccordionChildren } from "./useAccordion";
import createContext from "../../utils/createContext";

const [AccordionContext, useAccordionContext] = createContext({
  name: "Accordion",
});

/**
 *
 * Returns a container accordion for `AccordionItem` components. This component manages
 * which items are open, and offers options such as `allowToggle` to only allow
 * items to be opened or `allowMultiple` to allow multiple to opened at one time.
 *
 * Please note: the direct children of this component **must** be an `AccordionItem`.
 */

function Accordion(props) {
  const { className, ...rest } = props;
  const { htmlProps, ...context } = useAccordion(rest);
  const children = useAccordionChildren(props.children);
  const classNames = cx(styles.accordion, className);
  return (
    <AccordionContext value={context}>
      <div className={classNames} {...htmlProps}>
        {children}
      </div>
    </AccordionContext>
  );
}

export default Accordion;

export { useAccordionContext };

Accordion.propTypes = {
  /** If true, multiple items can be open at the same time. Defaults to false */
  allowMultiple: bool,
  /** If true, items can be open and closed. Defaults to false */
  allowToggle: bool,
  /** Change the style of the accordion */
  backgroundStyle: oneOf(["solid", "ghost"]),
  /** For CSS customisation */
  className: string,
  /** Pass a single number or array of numbers to open by default. Starts from 0. */
  defaultOpen: oneOfType([number, arrayOf(number)]),
};
